<template>
  <form>
    <div class="category-top-bar sticky-header">
      <div class="top-bar">
        <p>
          <a class="btn btn-orange btn-icon back" :title="$t('general.back')" @click.prevent="backButton">
            <i class="ti-arrow-left"></i>
            {{ $t('general.back') }}
          </a>
        </p>
        <p class="d-inline-flex">
          <a class="btn btn-danger btn-icon btn mar-rt-10 delete" v-if="id" @click.prevent="deleteCampaign">
            <img src="/static/img/icons/icon-close.png">
            {{ $t('general.delete') }}
          </a>
          <button type="submit" class="btn btn-success btn-icon mar-rt-10 save" @click.prevent="validate" :title="$t('general.save')">
            <img src="/static/img/icons/save.png">
            {{ $t('general.save') }}
          </button>
        </p>
      </div>
    </div>

    <div class="row mar-to-15 mar-bt-15 donation-campaign-form">
      <div class="col-md-12">
        <div class="card donation-campaign-form__card">
          <div :class="(loading ? 'show-loader' : 'hide-loader')">
            <div class="el-loading-spinner">
              <svg viewBox="25 25 50 50" class="circular">
                <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
              </svg>
            </div>
          </div>
          <div class="card-content">
            <div class="header donation-campaign-form__header">
              <h5>{{ $t('donationCampaigns.details') }}</h5>
            </div>
            <p class="text-danger" v-if="errors.has('error')">{{ getError('error') }}</p>

            <div class="row">
              <div class="col-lg-6 col-md-12">
                <div class="form-group">
                  <label>{{ $t('donationCampaigns.name') }}</label>
                  <input type="text"
                         name="name"
                         :placeholder="$t('donationCampaigns.name')"
                         v-model="model.name"
                         v-validate="modelValidations.name"
                         class="form-control">
                  <small class="text-danger" v-if="errors.has('name')">{{ getError('name') }}</small>
                </div>

                <div class="form-group">
                  <label>{{ $t('donationCampaigns.description') }}</label>
                  <textarea
                    rows="4"
                    name="description"
                    :placeholder="$t('donationCampaigns.description')"
                    v-model="model.description"
                    class="form-control">
                  </textarea>
                </div>

                <div class="form-group">
                  <label>{{ $t('donationCampaigns.targetAmount') }} ({{ currency }})</label>
                  <input type="number"
                         min="0"
                         step="0.01"
                         name="target_amount"
                         :placeholder="$t('donationCampaigns.targetAmount')"
                         v-model="model.target_amount"
                         v-validate="modelValidations.target_amount"
                         class="form-control">
                  <small class="text-danger" v-if="errors.has('target_amount')">{{ getError('target_amount') }}</small>
                </div>

                <div class="form-group">
                  <label>{{ $t('donationCampaigns.fundingMode') }}</label>
                  <el-select
                    v-model="model.funding_mode"
                    style="width: 100%;"
                    :placeholder="$t('donationCampaigns.fundingMode')">
                    <el-option
                      v-for="option in fundingModeOptions"
                      :key="option.value"
                      :label="option.label"
                      :value="option.value">
                    </el-option>
                  </el-select>
                </div>

                <div class="form-group" v-if="model.funding_mode === 'restaurant_flat'">
                  <label>{{ $t('donationCampaigns.restaurantFlatAmount') }} ({{ currency }})</label>
                  <input type="number"
                         min="0"
                         step="0.01"
                         name="restaurant_flat_amount"
                         :placeholder="$t('donationCampaigns.restaurantFlatAmount')"
                         v-model="model.restaurant_flat_amount"
                         v-validate="'required|min_value:0.01'"
                         class="form-control">
                  <small class="text-danger" v-if="errors.has('restaurant_flat_amount')">{{ getError('restaurant_flat_amount') }}</small>
                </div>

                <div class="form-group" v-if="model.funding_mode === 'restaurant_percent'">
                  <label>{{ $t('donationCampaigns.restaurantPercent') }}</label>
                  <input type="number"
                         min="0"
                         max="100"
                         step="0.01"
                         name="restaurant_percent"
                         :placeholder="$t('donationCampaigns.restaurantPercent')"
                         v-model="model.restaurant_percent"
                         v-validate="'required|decimal|min_value:0|max_value:100'"
                         class="form-control">
                  <small class="text-danger" v-if="errors.has('restaurant_percent')">{{ getError('restaurant_percent') }}</small>
                </div>
              </div>

              <div class="col-lg-6 col-md-12">
                <div class="form-group">
                  <label>{{ $t('donationCampaigns.image') }}</label>
                  <el-upload
                    v-if="!loading"
                    action=""
                    ref="upload"
                    :limit="2"
                    :file-list="model.imagelist"
                    list-type="picture-card"
                    accept=".jpg, .jpeg, .png"
                    :on-change="handleImgChange"
                    :on-exceed="handleExceed"
                    :on-remove="handleRemove"
                    :auto-upload="false">
                    <img src="/static/img/icons/icon-plus-primary.png">
                    <div class="el-upload__tip" slot="tip">{{ $t('validations.pictureSize') }}{{ $t('validations.imageResolution') }}</div>
                  </el-upload>
                  <small class="text-danger" v-if="errors.has('image')">{{ getError('image') }}</small>
                </div>

                <div class="form-group">
                  <label>{{ $t('donationCampaigns.startEnd') }}</label>
                  <el-date-picker
                    type="daterange"
                    v-model="model.fromto"
                    format="yyyy-MM-dd"
                    value-format="yyyy-MM-dd"
                    :picker-options="pickerOptions"
                    style="width: 100%;">
                  </el-date-picker>
                </div>

                <div class="form-group">
                  <label>{{ $t('donationCampaigns.location') }}</label>
                  <v-select v-model="locations_formatted"
                            multiple
                            :options="locations"
                            label="name">
                  </v-select>
                  <small><i class="fa fa-info-circle" aria-hidden="true"></i> {{ $t('donationCampaigns.locationInfo') }}</small>
                </div>

                <div class="form-group">
                  <label>{{ $t('donationCampaigns.active') }}</label>
                  <p-switch v-model="model.active" type="success"></p-switch>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import Vue from 'vue'
import { Input, Upload, Button, DatePicker, Select, Option } from 'element-ui'
import PSwitch from 'src/components/UIComponents/Switch.vue'
import vSelect from 'vue-select'
import swal from 'sweetalert2'
import 'sweetalert2/dist/sweetalert2.css'
import moment from 'moment'

Vue.component('v-select', vSelect)
Vue.use(DatePicker)
Vue.use(Upload)
Vue.use(Button)
Vue.use(Input)
Vue.use(Select)

export default {
  components: {
    [Button.name]: Button,
    [Input.name]: Input,
    [Select.name]: Select,
    [Option.name]: Option,
    PSwitch
  },
  props: {
    route: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      id: this.route.params.id || null,
      currency: this.getCurrencySymbol(this.$session.get('selectedCompanyData')['currency_iso']),
      loading: true,
      locations: [],
      locations_formatted: [],
      model: {
        name: '',
        description: '',
        target_amount: null,
        fromto: [],
        active: true,
        imagelist: [],
        image: null,
        location_ids: [],
        funding_mode: 'client_optional',
        restaurant_flat_amount: null,
        restaurant_percent: null
      },
      modelValidations: {
        name: {
          required: true,
          min: 3
        },
        target_amount: {
          required: true,
          min_value: 0
        }
      },
      pickerOptions: {
        disabledDate (time) {
          if ((time.getTime() + 86401 * 1000) < Date.now()) {
            return true
          }
          return false
        }
      }
    }
  },
  computed: {
    fundingModeOptions () {
      return [
        {
          value: 'client_optional',
          label: this.$t('donationCampaigns.fundingModeClientOptional')
        },
        {
          value: 'restaurant_flat',
          label: this.$t('donationCampaigns.fundingModeRestaurantFlat')
        },
        {
          value: 'restaurant_percent',
          label: this.$t('donationCampaigns.fundingModeRestaurantPercent')
        }
      ]
    }
  },
  watch: {
    'model.funding_mode' (mode) {
      if (mode !== 'restaurant_flat') {
        this.model.restaurant_flat_amount = null
        this.errors.remove('restaurant_flat_amount')
      }
      if (mode !== 'restaurant_percent') {
        this.model.restaurant_percent = null
        this.errors.remove('restaurant_percent')
      }
    }
  },
  created () {
    this.loading = true
    this.getLocations().then(() => {
      if (this.id) {
        this.getCampaign()
      } else {
        this.loading = false
      }
    }).catch(() => {
      this.loading = false
    })
  },
  methods: {
    setDonationCampaignImagelist (item) {
      if (item.image_url) {
        item.imagelist = item.image_url
      } else if (item.image) {
        item.imagelist = [{ url: item.image }]
      } else {
        item.imagelist = []
      }
      if (!Array.isArray(item.imagelist)) {
        item.imagelist = item.imagelist ? [{ url: String(item.imagelist) }] : []
      }
    },
    getLocations () {
      return new Promise((resolve, reject) => {
        const data = {
          token: this.$session.get('token'),
          company_id: this.$session.get('selectedCompany')
        }
        this.apiRequest('/get-locations', 'GET', data, (response) => {
          if (response.status) {
            this.locations = response.data
            resolve()
          } else {
            reject(new Error('Failed to fetch locations'))
          }
        })
      })
    },
    getCampaign () {
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }
      this.apiRequest('/reports/donation-campaigns/' + this.id, 'GET', data, (response) => {
        if (response.status === true) {
          const item = (response.data && response.data[0]) ? response.data[0] : response.data
          const startAt = item.starts_at || item.date_start
          const endAt = item.ends_at || item.date_end
          if (startAt && endAt) {
            item.fromto = [this.parseCampaignDateForPicker(startAt), this.parseCampaignDateForPicker(endAt)]
          } else {
            item.fromto = []
          }
          this.setDonationCampaignImagelist(item)
          item.active = !!(parseInt(item.active) === 1 || item.active === true)
          if (!item.funding_mode) {
            item.funding_mode = 'client_optional'
          }
          this.model = item
          if (item.locations_formatted) {
            this.locations_formatted = item.locations_formatted
          } else if (item.location_ids && this.locations.length) {
            this.locations_formatted = this.locations.filter(loc => item.location_ids.includes(loc.id))
          }
        }
        this.$nextTick(() => {
          this.loading = false
        })
      })
    },
    getError (fieldName) {
      return this.errors.first(fieldName)
    },
    parseCampaignDateForPicker (value) {
      if (!value) return null
      return moment(String(value)).format('YYYY-MM-DD')
    },
    formatCampaignStartsAt (date) {
      if (!date) return null
      return moment(String(date)).format('YYYY-MM-DD') + ' 00:00:00'
    },
    formatCampaignEndsAt (date) {
      if (!date) return null
      return moment(String(date)).format('YYYY-MM-DD') + ' 23:59:59'
    },
    validate () {
      this.$validator.validateAll().then(isValid => {
        if (isValid) {
          this.errors.clear()
          this.saveCampaign()
        }
      })
    },
    isActiveCampaignExistsResponse (response) {
      return !!(response && response.errors && response.errors.code === 'ACTIVE_CAMPAIGN_EXISTS' && response.data)
    },
    handleActiveCampaignExists (response) {
      const savedCampaign = response.data
      const activeCampaign = savedCampaign.active_campaign || {}
      const campaignId = savedCampaign.id || this.id

      this.model.active = false
      if (!this.id && campaignId) {
        this.id = campaignId
      }

      const message = this.$t('donationCampaigns.activeCampaignExistsMessage', { name: activeCampaign.name || '' })
      const warning = this.$t('donationCampaigns.activeCampaignExistsWarning')

      swal({
        title: this.$t('donationCampaigns.activeCampaignExistsTitle'),
        html: message + '<br><br><strong>' + warning + '</strong>',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('general.yes'),
        cancelButtonText: this.$t('general.no')
      }).then((result) => {
        if (result) {
          this.activateCampaign(campaignId)
        } else {
          this.backButton()
        }
      }).catch(() => {
        this.backButton()
      })
    },
    activateCampaign (campaignId) {
      this.loading = true
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        active: 1,
        force_active: true
      }
      this.apiRequest('/reports/donation-campaigns/' + campaignId, 'PUT', data, (response) => {
        if (response.status === true) {
          this.loading = false
          swal({
            title: '',
            text: this.$t('donationCampaigns.campaignUpdated'),
            type: 'success',
            showConfirmButton: false,
            timer: 1500
          }).catch(() => {})
          this.backButton()
        } else if (this.isActiveCampaignExistsResponse(response)) {
          this.loading = false
          this.handleActiveCampaignExists(response)
        } else {
          this.loading = false
          swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
        }
      })
    },
    handleErrors (errors) {
      for (const prop in errors) {
        if (Object.prototype.hasOwnProperty.call(errors, prop)) {
          if (Array.isArray(errors[prop]) && errors[prop].length === 1) {
            this.errors.add(prop, errors[prop][0])
          } else {
            this.errors.add(prop, errors[prop])
          }
        }
      }
    },
    saveCampaign () {
      this.loading = true
      const locationIds = (this.locations_formatted || []).map(loc => loc.id)
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        name: this.model.name,
        description: this.model.description,
        target_amount: this.model.target_amount,
        active: this.model.active ? 1 : 0,
        location_ids: locationIds,
        funding_mode: this.model.funding_mode
      }

      if (this.model.funding_mode === 'restaurant_flat') {
        data.restaurant_flat_amount = this.model.restaurant_flat_amount
      } else if (this.model.funding_mode === 'restaurant_percent') {
        data.restaurant_percent = this.model.restaurant_percent
      }

      if (this.model.fromto && this.model.fromto[0] && this.model.fromto[1]) {
        data.starts_at = this.formatCampaignStartsAt(this.model.fromto[0])
        data.ends_at = this.formatCampaignEndsAt(this.model.fromto[1])
      } else {
        data.starts_at = null
        data.ends_at = null
      }

      if (this.model.image) {
        data.image = this.model.image
      } else if (Array.isArray(this.model.imagelist) && this.model.imagelist.length === 0) {
        data.image = ''
      }

      const url = this.id ? '/reports/donation-campaigns/' + this.id : '/reports/donation-campaigns'
      const httpMethod = this.id ? 'PUT' : 'POST'

      this.apiRequest(url, httpMethod, data, (response) => {
        if (response.status === true) {
          this.loading = false
          swal({
            title: '',
            text: this.id ? this.$t('donationCampaigns.campaignUpdated') : this.$t('donationCampaigns.campaignAdded'),
            type: 'success',
            showConfirmButton: false,
            timer: 1500
          }).catch(() => {})
          this.backButton()
        } else if (this.isActiveCampaignExistsResponse(response)) {
          this.loading = false
          this.handleActiveCampaignExists(response)
        } else {
          this.loading = false
          if (response.errors) {
            this.handleErrors(response.errors)
          } else {
            swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
          }
        }
      })
    },
    deleteCampaign () {
      let btnCounter = 3
      swal({
        text: this.$t('donationCampaigns.areYouSureDelete') + ' ' + (this.model.name || '') + '?',
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('general.yes') + ' (' + btnCounter + ')'
      }).then((result) => {
        if (result) {
          const data = {
            token: this.$session.get('token'),
            company_id: this.$session.get('selectedCompany')
          }
          this.apiRequest('/reports/donation-campaigns/' + this.id, 'DELETE', data, (response) => {
            if (response.status === true) {
              swal('', this.$t('donationCampaigns.campaignDeleted'), 'success').catch(() => {})
              this.backButton()
            } else {
              swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
            }
          })
        }
      }).catch(() => {})
      swal.disableConfirmButton()
      const btnInterval = setInterval(() => {
        btnCounter--
        if (btnCounter >= 1) {
          swal.getConfirmButton().textContent = this.$t('general.yes') + ' (' + btnCounter + ')'
        } else {
          clearInterval(btnInterval)
          swal.getConfirmButton().textContent = this.$t('general.yes')
          swal.enableConfirmButton()
        }
      }, 1000)
    },
    handleImgChange (file, fileList) {
      const isOkSize = file.size / 1024 / 1024 < 5
      if (!isOkSize) {
        swal({
          toast: true,
          type: 'warning',
          text: this.$t('validations.pictureSizeExceed'),
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000
        })
        this.$refs.upload.clearFiles()
        this.model.imagelist = []
        return
      }
      if (fileList.length > 1) {
        this.model.imagelist = fileList.slice(1)
      } else {
        this.model.imagelist = fileList
      }
      if (this.model.imagelist && this.model.imagelist[0] && this.model.imagelist[0].status === 'ready') {
        this.getBase64(this.model.imagelist[0].raw).then((base64enc) => {
          this.model.image = base64enc
        })
      }
    },
    handleRemove (file, fileList) {
      if (fileList.length === 0) {
        this.model.imagelist = []
        this.model.image = null
      }
    },
    handleExceed () {
      swal({
        text: this.$t('donationCampaigns.removeCurrentImage'),
        type: 'warning',
        showConfirmButton: true
      })
    },
    backButton () {
      this.$bus.$emit('refresh')
      this.$bus.$emit('close-modal')
    }
  }
}
</script>

<style scoped>
.donation-campaign-form__card {
  height: 100%;
  width: 100%;
  margin: 0;
}

.donation-campaign-form__header {
  margin: -15px 0 30px !important;
}

.el-date-editor {
  width: 100% !important;
}

.content {
  padding: 0 !important;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Marketing/DonationCampaigns/DonationCampaignForm.vue